export type AppointmentContactPresentation = {
  contactName: string | null
  contactPhone: string | null
  contactEmail: string | null
  contactSourceLabel: string | null
  confirmedByLabel: string | null
  confirmedAt: string | null
  confidence: string
}

const clean = (value: unknown): string | null => {
  if (value === null || value === undefined) return null

  const text = String(value).trim()

  if (text === "—" || text === "-") return null

  return text || null
}

const calendarProviderLabel = (value: string): string => {
  switch (value.trim().toLowerCase()) {
    case "google":
      return "Google"
    case "microsoft":
      return "Microsoft Outlook"
    default:
      return "Kalender"
  }
}

const sourceLabel = (source?: string | null, event?: ScheduleEvent | null) => {
  switch (clean(source)?.toUpperCase()) {
    case "WEB_BOT":
      return "Chatbot"
    case "VOICE_BOT":
      return "Voicebot"
    case "AUTO_PLANNER":
      return "Planung"
    case "SYSTEM":
      return "System"
    case "CALENDAR_SYNC":
      return event?.calendar_sync?.links?.[0]?.provider
        ? calendarProviderLabel(event.calendar_sync.links[0].provider)
        : "Kalender"
    case "MANUAL":
    case "ADMIN":
      return "KehrBlatt"
    case "PORTAL":
      return "Portal"
    default:
      return null
  }
}

const fromBackendSummary = (
  event?: ScheduleEvent | null,
): AppointmentContactPresentation | null => {
  const onsite = event?.onsite_contact ?? null
  const confirmed = event?.confirmed_by_participant ?? null

  if (onsite || confirmed) {
    const confirmedBase = clean(confirmed?.display_name) || clean(confirmed?.email)
    const via = sourceLabel(confirmed?.source, event)
    const presentation = {
      contactName: clean(onsite?.display_name),
      contactPhone: clean(onsite?.phone),
      contactEmail: clean(onsite?.email),
      contactSourceLabel: onsite ? "Vor Ort" : null,
      confirmedByLabel: confirmedBase && via ? `${confirmedBase} über ${via}` : confirmedBase,
      confirmedAt: clean(event?.lifecycle_summary?.confirmed_at),
      confidence: clean(onsite?.confidence) ?? "explicit",
    }

    return hasAppointmentContact(presentation) || presentation.confirmedByLabel
      ? presentation
      : null
  }

  const summary = event?.appointment_contact ?? null

  if (!summary) return null

  const presentation = {
    contactName: clean(summary.contact_name),
    contactPhone: clean(summary.contact_phone),
    contactEmail: clean(summary.contact_email),
    contactSourceLabel: clean(summary.contact_source_label),
    confirmedByLabel: clean(summary.confirmed_by_label),
    confirmedAt: clean(summary.confirmed_at),
    confidence: clean(summary.confidence) ?? "none",
  }

  return hasAppointmentContact(presentation) || presentation.confirmedByLabel ? presentation : null
}

export const hasAppointmentContact = (contact?: AppointmentContactPresentation | null) =>
  Boolean(contact?.contactName || contact?.contactPhone || contact?.contactEmail)

export const resolveAppointmentContact = ({
  event,
  workOrder,
  customersById = {},
  contactPoints = [],
}: {
  event?: ScheduleEvent | null
  workOrder?: WorkOrder | null
  customersById?: Record<string, Customer | undefined>
  contactPoints?: ContactPoint[]
}): AppointmentContactPresentation | null => {
  void workOrder
  void customersById
  void contactPoints
  const backend = fromBackendSummary(event)

  if (backend) return backend

  return null
}

export const truncateTagLabel = (value: string | null | undefined, maxLength = 8) => {
  const text = clean(value) ?? ""

  if (text.length <= maxLength) return text

  return `${text.slice(0, Math.max(0, maxLength - 3)).trimEnd()}...`
}

export const shortPersonLabel = (value: string | null | undefined, maxLength = 8) => {
  const text = clean(value)

  if (!text) return null

  return truncateTagLabel(text, maxLength)
}

export const buildAppointmentSubLabel = ({
  employeeLabel,
  contactName,
  prefixEmployeeOnly = false,
}: {
  employeeLabel?: string | null
  contactName?: string | null
  prefixEmployeeOnly?: boolean
}) => {
  const contact = shortPersonLabel(contactName)
  const employee = contact ? shortPersonLabel(employeeLabel) : clean(employeeLabel)

  if (!contact && employee && prefixEmployeeOnly) return `Mitarbeiter: ${employee}`

  return [employee, contact].filter(Boolean).join(" · ") || null
}
